import { useId, useState } from 'react'

export interface ScopeOption {
  id: string
  label: string
}

export function ScopeCombobox({ label, value, options, onChange, placeholder, disabled, autoFocus }: {
  label: string
  value: string
  options: ScopeOption[]
  onChange: (id: string) => void
  placeholder: string
  disabled?: boolean
  autoFocus?: boolean
}) {
  const listId = useId()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const selected = options.find((option) => option.id === value)
  const normalize = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  const filtered = options.filter((option) => normalize(option.label).includes(normalize(query.trim())))

  const choose = (id: string) => {
    onChange(id)
    setQuery('')
    setOpen(false)
  }

  return (
    <div className="fz-auth-field">
      <label htmlFor={`${listId}-input`}>{label}</label>
      <div className="fz-scope-search">
        <input
          id={`${listId}-input`}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={open && filtered[activeIndex] ? `${listId}-${filtered[activeIndex].id}` : undefined}
          autoComplete="off"
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder}
          value={open ? query : selected?.label ?? ''}
          onFocus={() => { setQuery(''); setActiveIndex(0); setOpen(true) }}
          onBlur={() => { setOpen(false); setQuery('') }}
          onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); setOpen(true) }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              setOpen(true)
              setActiveIndex((index) => Math.max(0, Math.min(index + 1, filtered.length - 1)))
            } else if (event.key === 'ArrowUp') {
              event.preventDefault()
              setOpen(true)
              setActiveIndex((index) => Math.max(index - 1, 0))
            } else if (event.key === 'Enter' && open) {
              event.preventDefault()
              if (filtered[activeIndex]) {choose(filtered[activeIndex].id)}
            } else if (event.key === 'Escape') {
              setOpen(false)
              setQuery('')
              event.currentTarget.blur()
            }
          }}
        />
        {open && (
          <ul id={listId} role="listbox" aria-label={label} className="fz-select__list fz-scope-search__list" onMouseDown={(event) => event.preventDefault()}>
            {filtered.length === 0 ? (
              <li className="fz-select__opt is-disabled">Nenhuma opção encontrada</li>
            ) : filtered.map((option, index) => (
              <li
                id={`${listId}-${option.id}`}
                key={option.id}
                role="option"
                aria-selected={option.id === value}
                className={'fz-select__opt' + (index === activeIndex ? ' is-active' : '') + (option.id === value ? ' is-selected' : '')}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => choose(option.id)}
              >{option.label}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
